import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';

import emptySelectOption from '@/constants/emptySelectOption';
import usePhaseTypes from '@/hooks/usePhaseTypes';
import usePrimarySubstances from '@/hooks/usePrimarySubstances';
import usePrograms from '@/hooks/usePrograms';
import PersonContext from '@/pages/Person/PersonContext';
import { InputGroup, Select } from '@/ui';
import { OptionType } from '@/ui/components/Form/Select/types';
import snakeToTitleCase from '@/utils/snakeToTitleCase';

import type { Props } from './types';

const ProgramSelect = ({ onPrimarySubstanceChange, onProgramChange }: Props) => {
  const { person } = useContext(PersonContext);
  const [selectedPrimarySubstance, setSelectedPrimarySubstance] =
    useState<OptionType<string>>(emptySelectOption);
  const [selectedPhaseType, setSelectedPhaseType] = useState<OptionType<string>>(emptySelectOption);
  const [selectedProgram, setSelectedProgram] = useState<OptionType<string>>(emptySelectOption);
  const [optionChangedByInput, setOptionChangedByInput] = useState(false);

  const { data: primarySubstanceResponse } = usePrimarySubstances({ visible_to_members: true });
  const primarySubstancesForSelect = useMemo(() => {
    return (
      primarySubstanceResponse?.data?.map(({ id, name }) => ({
        label: name,
        value: id
      })) ?? []
    );
  }, [primarySubstanceResponse?.data]);

  const selectedDrugCategory = useMemo(() => {
    if (!selectedPrimarySubstance?.value) return null;
    return primarySubstanceResponse?.data?.find(
      (substance) => substance.id === selectedPrimarySubstance.value
    )?.drug_category;
  }, [primarySubstanceResponse?.data, selectedPrimarySubstance.value]);

  const { data: programsResponse } = usePrograms(
    {
      phase_type_id: selectedPhaseType.value ?? '',
      drug_category: selectedDrugCategory ?? ''
    },
    Boolean(selectedPhaseType?.value) && Boolean(selectedDrugCategory)
  );

  const programsForSelect = useMemo(
    () =>
      programsResponse?.data?.map((program) => ({
        value: program.id,
        label: program.name
      })) ?? [],
    [programsResponse?.data]
  );

  const { data: phaseTypesResponse } = usePhaseTypes(
    selectedDrugCategory ? { drug_category: selectedDrugCategory, unique: true } : {},
    Boolean(selectedDrugCategory)
  );

  const phaseTypesForSelect = useMemo(
    () =>
      phaseTypesResponse?.data?.map((phaseType) => ({
        value: phaseType.id,
        label: snakeToTitleCase(phaseType.name)
      })) ?? [],
    [phaseTypesResponse?.data]
  );

  const deselectProgram = () => {
    setSelectedProgram(emptySelectOption);
    onProgramChange(null, undefined, '');

    setSelectedPhaseType(emptySelectOption);
  };

  const onPrimarySubstanceChangeHandler = (option: OptionType<string> | null) => {
    setOptionChangedByInput(true);
    const newOption = option ?? emptySelectOption;
    if (newOption.value !== selectedPrimarySubstance.value) {
      deselectProgram();
    }

    setSelectedPrimarySubstance(newOption);
    onPrimarySubstanceChange(newOption.value || null);
    setSelectedPhaseType(emptySelectOption);
  };

  const onProgramChangeHandler = useCallback(
    (option: OptionType<string> | null) => {
      setOptionChangedByInput(true);
      const newOption = option ?? emptySelectOption;
      setSelectedProgram(newOption ?? emptySelectOption);
      onProgramChange(newOption?.value || null, newOption?.label, selectedPhaseType.label);
    },
    [onProgramChange, selectedPhaseType.label]
  );

  const onPhaseTypeChangeHandler = (option: OptionType<string> | null) => {
    setOptionChangedByInput(true);
    setSelectedPhaseType(option ?? emptySelectOption);
    setSelectedProgram(emptySelectOption);
    onProgramChange(null, undefined, '');
  };

  // Set default Primary Substance
  useEffect(() => {
    if (
      !optionChangedByInput &&
      primarySubstancesForSelect.length &&
      selectedPrimarySubstance.value === emptySelectOption.value &&
      person.program_qualification?.primary_substance
    ) {
      const defaultSubstance =
        primarySubstancesForSelect.find(
          (option) => option.label === person.program_qualification?.primary_substance
        ) ?? emptySelectOption;
      setSelectedPrimarySubstance(defaultSubstance);
      onPrimarySubstanceChange(defaultSubstance.value);
    }
  }, [
    primarySubstancesForSelect,
    onPrimarySubstanceChange,
    selectedPrimarySubstance.value,
    person.program_qualification?.primary_substance,
    optionChangedByInput,
    selectedPrimarySubstance
  ]);

  // Set default Program
  useEffect(() => {
    const shouldSetDefaultProgram =
      !optionChangedByInput &&
      programsForSelect.length > 0 &&
      selectedProgram.value === emptySelectOption.value &&
      Boolean(person.program?.id) &&
      Boolean(selectedPhaseType.value);

    if (shouldSetDefaultProgram) {
      const defaultProgram =
        programsForSelect.find((option) => option.value === person.program?.id) ??
        emptySelectOption;

      if (defaultProgram.value === emptySelectOption.value) {
        return;
      }

      setSelectedProgram(defaultProgram);
      onProgramChange(defaultProgram.value, defaultProgram.label, selectedPhaseType.label);
    }
  }, [
    optionChangedByInput,
    onProgramChange,
    person.program?.id,
    programsForSelect,
    selectedPhaseType,
    selectedPhaseType.label,
    selectedProgram.value
  ]);

  // Set default Phase Type
  useEffect(() => {
    if (optionChangedByInput || selectedPhaseType.value !== emptySelectOption.value) {
      return; // Don't set default if user interacted or already selected
    }

    if (phaseTypesForSelect.length > 0 && person.program?.phase_type_id) {
      const defaultPhaseType =
        phaseTypesForSelect.find(
          (option) => option.value?.toLowerCase() === person.program?.phase_type_id
        ) || emptySelectOption;

      if (defaultPhaseType.value !== selectedPhaseType.value) {
        setSelectedPhaseType(defaultPhaseType);
      }
    }
  }, [
    optionChangedByInput,
    person.program?.phase_type_id,
    phaseTypesForSelect,
    selectedPhaseType,
    selectedPhaseType.value
  ]);

  // Set default Program when only one program is available
  useEffect(() => {
    if (
      optionChangedByInput &&
      selectedPhaseType.value !== emptySelectOption.value &&
      programsForSelect.length === 1
    ) {
      const onlyProgram = programsForSelect[0];
      setSelectedProgram(onlyProgram);
      onProgramChangeHandler(onlyProgram);
    }
  }, [programsForSelect, selectedPhaseType, optionChangedByInput, onProgramChangeHandler]);

  return (
    <>
      <InputGroup>
        <Select
          clearable
          label="Primary Concern"
          onChange={onPrimarySubstanceChangeHandler}
          options={primarySubstancesForSelect}
          placeholder="Select Primary Concern"
          selectedOption={selectedPrimarySubstance}
        />
      </InputGroup>

      <InputGroup>
        <Select
          clearable
          disabled={!selectedPrimarySubstance?.value}
          label="Phase Type"
          onChange={onPhaseTypeChangeHandler}
          options={phaseTypesForSelect}
          placeholder="Select Phase Type"
          selectedOption={selectedPhaseType}
        />
      </InputGroup>

      <InputGroup>
        <Select
          clearable
          disabled={!selectedPhaseType?.value}
          label="Program Family"
          onChange={onProgramChangeHandler}
          options={programsForSelect}
          placeholder="Select Program Family"
          selectedOption={selectedProgram}
        />
      </InputGroup>
    </>
  );
};
export default React.memo(ProgramSelect);
